import { logger } from '@gem/modules/logger';

import {
  getCalendarEventsRequest,
  deleteCalendarEventRequest,
  deleteRecurrenceEventRequest,
  createCalendarEventRequest,
  updateCalendarEventRequest,
  updateRecurrenceCalendarEventRequest,
  checkAuthorizationRequest,
  logoutCalendarRequest,
  updateEventParticipantStatusRequest,
  searchUsersAddToCalendarEventRequest,
  getUserSettingsCalendarRequest,
  changeCompactCalendarViewRequest,
  subscribeToCalendarPushRequest,
  unsubscribeFromCalendarPushRequest,
  getCalendarAuthUrlRequest,
} from 'APP/wsApi/wsAppApi/requests/calendarRequests';

import { buildModelError } from '../error/errorBuilder';
import { WsRequestError } from '../error/errors/WsRequestError';
import { mapPersonalizedUserResponse } from '../user/userModel.b2b.mapper';
import {
  ICalendarAuthAccount,
  ICreateCalendarEventParams,
  IDeleteCalendarEventParams,
  IDeleteRecurrenceEventParams,
  IEvent,
  IGetCalendarEventsParams,
  ILogoutCalendarParams,
  ISearchUsersAddToCalendarEventParams,
  ISearchUsersAddToCalendarEventResult,
  ISubscribeToCalendarPushParams,
  IUpdateCalendarEventParams,
  IUpdateEventParticipantStatusParams,
  IUpdateRecurrenceCalendarEventParams,
  CalendarModelErrorCode,
  ISetCompactCalendarViewParams,
  IUserSettings,
  IGetCalendarAuthUrlParams,
} from './calendar.types';
import {
  mapCalendarAccountsRequest,
  mapCalendarEvents,
  mapSubscribeToCalendarPushRequest,
  mapCreateCalendarEventParams,
  mapDeleteCalendarEventParams,
  mapDeleteRecurrenceEventParams,
  mapLogoutCalendarParams,
  mapUpdateCalendarEventParams,
  mapUpdateEventParticipantStatusParams,
  mapUpdateRecurrenceCalendarEventParams,
  mapGetCalendarAuthUrlParams,
} from './calendarModel.mapper';

export async function getCalendarEvents(params: IGetCalendarEventsParams): Promise<IEvent[]> {
  try {
    const result = await getCalendarEventsRequest(params);

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success') {
      return mapCalendarEvents(result.type.value.calendarEvents);
    } else {
      return [];
    }
  } catch (error) {
    logger.get('API').error('calendar.getCalendarEvents', error);
    throw buildModelError(error);
  }
}

export async function deleteCalendarEvent(params: IDeleteCalendarEventParams): Promise<string> {
  try {
    const result = await deleteCalendarEventRequest(mapDeleteCalendarEventParams(params));

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success') {
      return result.type.value.eventId;
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.deleteCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function deleteRecurrenceCalendarEvent(
  params: IDeleteRecurrenceEventParams
): Promise<string> {
  try {
    const result = await deleteRecurrenceEventRequest(mapDeleteRecurrenceEventParams(params));

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success') {
      return result.type.value.eventId;
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.deleteRecurrenceCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function createCalendarEvent(params: ICreateCalendarEventParams): Promise<IEvent> {
  try {
    const result = await createCalendarEventRequest(mapCreateCalendarEventParams(params));

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success' && result.type.value.calendarEvent) {
      return mapCalendarEvents([result.type.value.calendarEvent])[0];
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.createCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function updateCalendarEvent(params: IUpdateCalendarEventParams): Promise<IEvent> {
  try {
    const result = await updateCalendarEventRequest(mapUpdateCalendarEventParams(params));

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success') {
      return mapCalendarEvents(result.type.value.calendarEvent)[0];
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.updateCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function updateRecurrenceCalendarEvent(
  params: IUpdateRecurrenceCalendarEventParams
): Promise<IEvent> {
  try {
    const result = await updateRecurrenceCalendarEventRequest(
      mapUpdateRecurrenceCalendarEventParams(params)
    );

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success') {
      return mapCalendarEvents(result.type.value.calendarEvent)[0];
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.updateRecurrenceCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function subscribeToCalendarPush(
  params: ISubscribeToCalendarPushParams
): Promise<boolean> {
  try {
    const result = await subscribeToCalendarPushRequest(
      mapSubscribeToCalendarPushRequest(params.accounts)
    );

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
      case 'success':
        return true;
      default:
        throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.subscribeToCalendarPush', error);
    throw buildModelError(error);
  }
}

export async function unsubscribeFromCalendarPush(
  params: ISubscribeToCalendarPushParams
): Promise<boolean> {
  try {
    const result = await unsubscribeFromCalendarPushRequest({
      account: mapCalendarAccountsRequest(params.accounts),
    });

    if (result.type.case === 'success') {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    logger.get('API').error('calendar.unsubscribeFromCalendarPush', error);
    throw buildModelError(error);
  }
}

export async function checkCalendarAuthorization(): Promise<ICalendarAuthAccount[]> {
  try {
    const result = await checkAuthorizationRequest({});

    if (result.type.case === 'success') {
      return result.type.value.accounts.map((item) => ({
        account: item.account!,
        isActive: item.isActive,
      }));
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.checkCalendarAuthorization', error);
    throw buildModelError(error);
  }
}

export async function logoutCalendar(params: ILogoutCalendarParams): Promise<boolean> {
  try {
    const result = await logoutCalendarRequest(mapLogoutCalendarParams(params));

    if (result.type.case === 'success') {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    logger.get('API').error('calendar.logout', error);
    throw buildModelError(error);
  }
}

export async function updateCalendarEventParticipantStatus(
  params: IUpdateEventParticipantStatusParams
): Promise<IEvent> {
  try {
    const result = await updateEventParticipantStatusRequest(
      mapUpdateEventParticipantStatusParams(params)
    );

    switch (result.type.case) {
      case 'unauthorized':
        throw new WsRequestError(CalendarModelErrorCode.Unauthorized, result);
      case 'forbidden':
        throw new WsRequestError(CalendarModelErrorCode.Forbidden, result);
    }

    if (result.type.case === 'success' && result.type.value.calendarEvent) {
      return mapCalendarEvents([result.type.value.calendarEvent])[0];
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.updateCalendarEventParticipantStatus', error);
    throw buildModelError(error);
  }
}

export async function searchUsersAddToCalendarEvent(
  params: ISearchUsersAddToCalendarEventParams
): Promise<ISearchUsersAddToCalendarEventResult> {
  try {
    const searchResult = await searchUsersAddToCalendarEventRequest(params);

    if (searchResult.type.case === 'success') {
      return {
        cursor: searchResult.type.value.cursor,
        users: searchResult.type.value.users.map((user) => mapPersonalizedUserResponse(user)),
      };
    } else {
      return {
        cursor: '',
        users: [],
      };
    }
  } catch (error) {
    logger.get('API').error('calendar.searchUsersAddToCalendarEvent', error);
    throw buildModelError(error);
  }
}

export async function getUserCalendarSettings(): Promise<IUserSettings> {
  try {
    const result = await getUserSettingsCalendarRequest({});

    if (result.type.case === 'success') {
      return result.type.value;
    }

    throw result.type.value;
  } catch (error) {
    logger.get('API').error('calendar.getUserSettingsCalendar', error);
    throw buildModelError(error);
  }
}

export async function setCompactViewCalendar(
  params: ISetCompactCalendarViewParams
): Promise<boolean> {
  try {
    const result = await changeCompactCalendarViewRequest(params);

    if (result.type.case === 'success') {
      return result.type.value.isCompact;
    }

    throw result.type.value;
  } catch (error) {
    logger.get('API').error('calendar.setCompactViewCalendar', error);
    throw buildModelError(error);
  }
}

export async function getCalendarAuthUrl(params: IGetCalendarAuthUrlParams): Promise<string> {
  try {
    const result = await getCalendarAuthUrlRequest(mapGetCalendarAuthUrlParams(params));

    if (result.type.case === 'success') {
      return result.type.value.url;
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('calendar.getCalendarAuthUrl', error);
    throw buildModelError(error);
  }
}
